import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { showBadgeOnFavicon } from './AudioAlerts/faviconHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import { useAlert } from 'dashboard/composables';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
      'internal_chat.message.created': this.onInternalChatMessageCreated,
      'internal_chat.message.deleted': this.onInternalChatMessageDeleted,
      'contact_list.import_progress': this.onContactListImportProgress,
      'contact_list.import_complete': this.onContactListImportComplete,
      'lead_generation.started': this.onLeadGenerationStarted,
      'lead_generation.progress': this.onLeadGenerationProgress,
      'lead_generation.completed': this.onLeadGenerationCompleted,
      'lead_generation.error': this.onLeadGenerationError,
      'ai_event.processed': this.onAiEventProcessed,
      'ai_event.error': this.onAiEventError,
      'funnel_item.created': this.onFunnelItemCreated,
      'funnel_item.moved': this.onFunnelItemMoved,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  safeUpdateConversation = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.safeUpdateConversation(payload);
    }
    this.fetchConversationStats();
    this.app.$store.dispatch('funnels/updateFunnelItemFromConversation', payload);
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
    // Try to link funnel items by phone number
    this.app.$store.dispatch('funnels/linkFunnelItemByPhone', data);
  };

  onConversationRead = data => {
    this.safeUpdateConversation(data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
    // Update funnel items linked to this conversation
    this.app.$store.dispatch('funnels/updateFunnelItemFromMessage', {
      conversationId,
      message: data,
      lastActivityAt,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.safeUpdateConversation(data);
    this.fetchConversationStats();
    this.app.$store.dispatch('funnels/updateFunnelItemFromConversation', data);
  };

  onConversationUpdated = data => {
    this.safeUpdateConversation(data);
    this.fetchConversationStats();
    this.app.$store.dispatch('funnels/updateFunnelItemFromConversation', data);
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };

  onInternalChatMessageCreated = data => {
    const { internal_chat_id: chatId } = data;
    this.app.$store.dispatch('internalChat/addMessageFromSocket', {
      chatId,
      message: data,
    });

    // Notificação popup e som para novas mensagens do chat interno
    const activeChat = this.app.$store.getters['internalChat/getActiveChat'];
    const isActiveChatMessage = activeChat?.id === chatId;

    // Só notifica se não for o chat ativo
    if (!isActiveChatMessage) {
      // Mostra popup de notificação
      useAlert('Mensagem Recebida do Chat Interno');

      // Toca o som de alerta
      DashboardAudioNotificationHelper.playAudioAlert();

      // Atualiza o favicon com badge
      showBadgeOnFavicon();
    }
  };

  onInternalChatMessageDeleted = data => {
    const { internal_chat_id: chatId, message_id: messageId } = data;
    this.app.$store.dispatch('internalChat/removeMessageFromSocket', {
      chatId,
      messageId,
    });
  };

  onContactListImportProgress = data => {
    emitter.emit('contact_list.import_progress', data);
  };

  onContactListImportComplete = data => {
    emitter.emit('contact_list.import_complete', data);
    this.app.$store.dispatch('contactLists/get');
  };

  onLeadGenerationStarted = data => {
    emitter.emit('lead_generation.started', data);
  };

  onLeadGenerationProgress = data => {
    emitter.emit('lead_generation.progress', data);
  };

  onLeadGenerationCompleted = data => {
    emitter.emit('lead_generation.completed', data);
    this.app.$store.dispatch('contactLists/get');
    useAlert(
      `Lista IA "${data.contact_list_name}" criada com ${data.contacts_count} contatos!`
    );
  };

  onLeadGenerationError = data => {
    emitter.emit('lead_generation.error', data);
    useAlert(data.error || 'Erro na geração de leads com IA.');
  };

  onAiEventProcessed = data => {
    emitter.emit(BUS_EVENTS.AI_EVENT_PROCESSED, data);
  };

  onAiEventError = data => {
    emitter.emit(BUS_EVENTS.AI_EVENT_ERROR, data);
  };

  onFunnelItemCreated = data => {
    this.app.$store.dispatch('funnels/handleRealtimeItemCreated', data);
  };

  onFunnelItemMoved = data => {
    this.app.$store.dispatch('funnels/handleRealtimeItemMoved', data);
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
